import { useCallback, useRef } from 'react';

import useRect from '@lyearn/core/hooks/useRect';
import useScreen from '@lyearn/core/hooks/useScreen';
import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { Trash2Outline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { BaseButton, Input, LinkButton, Stack, Tooltip, Typography } from '@lyearn/ui';
import { PicklistValue } from '@/components/Form';
import { hideDefaultIcon } from '@/components/TimePicker';
import { handleNativeTimePicker } from '@/components/TimePicker/utils';
import { setWidth } from '@/utils/styles';

interface Props {
  autoFocus?: boolean;
  error?: string;
  index: number;
  value: string;
  options: PicklistValue[];
  onChange: (index: number, value: string) => void;
  onDelete: (index: number) => void;
}

const HabitReminderInput = (props: Props) => {
  const { t } = useTranslation();
  const { isMobile } = useScreen();
  const { autoFocus, error, index, value, options, onChange, onDelete } = props;
  const insideTooltipRef = useRef(false);
  const { ref, width } = useRect();
  const [open, { on, off }] = useToggle(false);

  const onTooltipClose = useCallback(() => {
    if (!insideTooltipRef.current) {
      off();
    }
  }, [off]);

  const onMouseEnter = useCallback(() => {
    insideTooltipRef.current = true;
  }, []);

  const onMouseLeave = useCallback(() => {
    insideTooltipRef.current = false;
  }, []);

  return (
    <Stack ref={ref} direction="col">
      <Tooltip
        disableHoverListener
        classes={{ tooltip: cn('max-w-none p-12', width && setWidth(width)) }}
        enterDelay={0}
        open={open}
        placement="bottom"
        title={
          options.length && !isMobile ? (
            <Stack
              direction="col"
              onClick={off}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}>
              <Typography className="ms-4" color="text-text-placeholder" variant="label-caps">
                {t('Habits:Editor.Suggested')}
              </Typography>
              <Stack className="mt-12 flex-wrap" gap={10}>
                {options.map((option) => {
                  return (
                    <BaseButton
                      key={option.value}
                      className="body-short-02 h-32 rounded-6 border border-solid border-border px-12 text-text-primary hover:bg-interactive-secondary active:bg-interactive-secondary-hover"
                      onClick={() => onChange(index, option.value)}>
                      {option.label}
                    </BaseButton>
                  );
                })}
              </Stack>
            </Stack>
          ) : null
        }
        variant="popover"
        onClose={onTooltipClose}
        onOpen={on}>
        <Input
          autoFocus={autoFocus}
          className={hideDefaultIcon}
          endAdornment={
            <LinkButton color={'internal'} onClick={() => onDelete(index)}>
              <Trash2Outline />
            </LinkButton>
          }
          error={!!error}
          size="large"
          type="time"
          value={value}
          onChangeText={(nextValue) => onChange(index, nextValue)}
          onKeyDown={handleNativeTimePicker}
        />
      </Tooltip>
      {error ? (
        <Typography className="mt-8 ms-8" color="text-text-error" variant="body-short-03">
          {error}
        </Typography>
      ) : null}
    </Stack>
  );
};

export default HabitReminderInput;
